import FormInputField from "@/components/lib/from-input-field";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "../../../../components/Layout/LeftBar2";
import { ChevronDown } from "lucide-react";
import { backofficeLinks } from "../../../../components/lib/SidebarLinks";
import { Link, useNavigate } from "react-router-dom";
import { blackUser } from "@/assets";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { SelectShiftDialog } from "@/components/dialog/staff-manage/all-staff-dialog";
import { Label } from "@/components/ui/label";
import FileUploader from "@/components/common/file-uploader";
import { addStaff, getAllRole } from "@/api/new-api/staff-management/staff/staff";
import { toast } from "react-hot-toast";
import { getAllShift } from "@/api/new-api/shift/shift";
import moment from "moment";
import useAuth from "@/hooks/useAuth";
import {
  DESIGNATION_OPTIONS,
  GENDER_OPTIONS,
  EMPLOYMENT_TYPE_OPTIONS,
  SHIFT_SCHEDULE_OPTIONS,
  SALARY_TYPE_OPTIONS,
  CURRENCY_OPTIONS,
  STAFF_DEFAULT_VALUES,
  formatTime,
  getSelectedShiftsList,
  addNewStaffSchema,
} from "./staff-helpers";

function AddNewStaff() {
  const [files, setFiles] = useState([]);
  const navigate = useNavigate();
  const [openShiftDialog, setOpenShiftDialog] = useState(false);
  const [selectedShift, setSelectedShift] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const form = useForm({
    resolver: zodResolver(addNewStaffSchema),
    mode: "onChange",
    defaultValues: STAFF_DEFAULT_VALUES,
  });

  const { reset } = form;

  const setShiftValue = (value) => {
    const numericValues = (value || []).map(Number);
    setSelectedShift(numericValues);
    form.setValue("shifts", numericValues, { shouldValidate: true });
  };

  const addStaffData = async (data) => {
    setLoading(true);
    try {
      const finalShifts = (selectedShift || data.shifts || []).map(Number);
      const payload = {
        ...data,
        role_id: Number(data.role_id),
        joining_date: moment(data.joining_date).format("YYYY-MM-DD"),
        restaurant_id: Number(user?.restaurant_id),
        shifts: finalShifts,
      };
      const res = await addStaff(payload);
      if (res.status === "success") {
        toast.success("Staff added successfully");
        navigate("/backoffice/staff-management/staff/all");
        reset();
      } else {
        toast.error(res.message || "Failed to add staff");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to add staff");
    } finally {
      setLoading(false);
    }
  };

  const fetchShiftsAndRoles = async () => {
    try {
      setLoading(true);
      const [shiftRes, roleRes] = await Promise.all([
        getAllShift().catch((err) => {
          console.error("Error fetching shifts:", err);
          return null;
        }),
        getAllRole().catch((err) => {
          console.error("Error fetching roles:", err);
          return null;
        }),
      ]);

      const shiftList = shiftRes?.data?.shift_schedules || shiftRes?.data || [];
      const roleList = roleRes?.data?.roles || roleRes?.data || [];

      setShifts(Array.isArray(shiftList) ? shiftList : []);
      setRoles(Array.isArray(roleList) ? roleList : []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShiftsAndRoles();
  }, []);

  const onShiftDialog = () => {
    setOpenShiftDialog(true);
  };

  const offshiftdialog = () => {
    setOpenShiftDialog(false);
  };

  const handleUploadComplete = (fileData) => {
    const fileId = fileData[0]?.file_id;
    if (!fileId) return;
    form.setValue("file_manager_id", fileId);
  };

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
      pageName={{ title: "Add New Staff" }}
      paths={[
        { name: "Back Office", path: "/backoffice/overview" },
        {
          name: "Staff Management",
          path: "/backoffice/staff-management/staff/all",
        },
        { name: "All Staff", path: "/backoffice/staff-management/staff/all" },
        { name: "Add Staff", path: "/backoffice/staff-management/staff/add" },
      ]}
    >
      <div className="w-full max-w-5xl mx-auto my-5 mb-10 px-4">
        <Card className="bg-cblack4 text-cwhite">
          <CardHeader>
            <CardTitle className=" text-cwhite">Add New Staff</CardTitle>
          </CardHeader>
          <Separator />
          <CardContent>
            <div className="mt-6 relative">
              <span className="text-cyellow border-b-2 border-cyellow">
                Basic Information
              </span>
              <Separator className="absolute" />
            </div>
            <div className="mt-5 flex gap-3 justify-start items-start bg-cblack3 p-4">
              <div className="h-28 w-28 overflow-hidden">
                <img
                  src={
                    files[0]?.file_url ||
                    files[0]?.file_manager_url ||
                    blackUser
                  }
                  alt="Profile Image"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="flex flex-col gap-1">
                <h2>Upload Profile Image</h2>
                <CardDescription>Image should be below 4 MB</CardDescription>
                <div className="mt-3">
                  <FileUploader
                    files={files}
                    multiple={false}
                    replace={true}
                    restaurantId={user?.restaurant_id}
                    onFilesChange={(updatedFiles) => setFiles(updatedFiles)}
                    onUploadComplete={handleUploadComplete}
                    setFiles={setFiles}
                    label="Upload Photo"
                    type="button"
                  />
                </div>
              </div>
            </div>
            <div className="mt-8">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(addStaffData)}
                  className="flex flex-col gap-8"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <FormInputField
                      name="first_name"
                      style="bg-cblack3"
                      placeholder={"Enter First Name"}
                      form={form}
                      type="text"
                      label={"First Name"}
                      required={true}
                    />

                    <FormInputField
                      name="last_name"
                      placeholder={"Enter Last Name"}
                      form={form}
                      type="text"
                      style="bg-cblack3"
                      label={"Last Name"}
                      required={true}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <FormInputField
                      name="designation"
                      style="bg-cblack3"
                      placeholder={"Designation/Role"}
                      form={form}
                      type="select"
                      selectOption={DESIGNATION_OPTIONS}
                      label={"Designation"}
                      required={true}
                    />
                    <FormInputField
                      name="role_id"
                      style="bg-cblack3"
                      placeholder={"Role Permission"}
                      form={form}
                      type="select"
                      selectOption={
                        roles?.length > 0
                          ? roles.map((role) => ({
                              label: role.name,
                              value: String(role.id),
                            }))
                          : []
                      }
                      label={"Role Permission"}
                      required={true}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <FormInputField
                      name="joining_date"
                      placeholder={"dd/mm/yy"}
                      form={form}
                      type="date"
                      style="bg-cblack3"
                      label={"Joining Date"}
                      required={true}
                    />
                    <FormInputField
                      name="gender"
                      style="bg-cblack3"
                      placeholder={"Gender"}
                      form={form}
                      type="select"
                      selectOption={GENDER_OPTIONS}
                      label={"Gender"}
                      required={true}
                    />

                    <FormInputField
                      name="email"
                      style="bg-cblack3"
                      placeholder={"Enter your Email"}
                      form={form}
                      type="text"
                      label={"Email Address"}
                      required={true}
                    />

                    <FormInputField
                      name="phone"
                      placeholder={"Enter 11 digit phone number"}
                      form={form}
                      type="text"
                      style="bg-cblack3"
                      required={true}
                      label={"Phone Number"}
                    />
                  </div>
                  <div>
                    <FormInputField
                      name="address"
                      placeholder={"Enter Your Full Address"}
                      form={form}
                      type="text"
                      style="bg-cblack3"
                      label={"Staff Full Address"}
                      required={true}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center justify-center">
                    <FormInputField
                      name="employment_type"
                      style="bg-cblack3"
                      placeholder={"Select Staff Type"}
                      form={form}
                      type="select"
                      selectOption={EMPLOYMENT_TYPE_OPTIONS}
                      label={"Staff Type"}
                      required={true}
                    />

                    <div className="space-y-2">
                      <Label className="text-sm font-medium">
                        Shift Schedule <span className="text-cred">*</span>
                      </Label>
                      <div
                        onClick={() => onShiftDialog()}
                        className={`bg-cblack3 border ${
                          form.formState.errors.shifts
                            ? "border-cred"
                            : "border-gray-700 hover:border-gray-500"
                        } rounded min-h-[38px] flex justify-between px-3 py-1.5 items-center cursor-pointer gap-2 transition-colors`}
                      >
                        {selectedShift && selectedShift.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5 items-center">
                            <span className="bg-cyellow/20 text-cyellow border border-cyellow/40 text-xs px-2 py-0.5 rounded font-medium">
                              {selectedShift.length}{" "}
                              {selectedShift.length === 1 ? "Shift" : "Shifts"}{" "}
                              Selected
                            </span>
                            {getSelectedShiftsList(shifts, selectedShift)
                              .slice(0, 2)
                              .map((s) => (
                                <span
                                  key={s.id}
                                  className="bg-neutral-800 text-neutral-300 text-xs px-2 py-0.5 rounded capitalize border border-neutral-700"
                                >
                                  {s.day?.slice(0, 3)}: {formatTime(s.start_time)} -{" "}
                                  {formatTime(s.end_time)}
                                </span>
                              ))}
                            {selectedShift.length > 2 && (
                              <span className="text-xs text-neutral-400">
                                +{selectedShift.length - 2} more
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-neutral-500 text-sm">
                            Select Shift Schedule
                          </span>
                        )}

                        <ChevronDown
                          className="opacity-50 shrink-0 ml-auto"
                          size={16}
                        />
                      </div>
                      {form.formState.errors.shifts && (
                        <p className="text-cred text-xs">
                          {form.formState.errors.shifts.message}
                        </p>
                      )}
                    </div>

                    <FormInputField
                      name="shift_schedule_id"
                      style="bg-cblack3"
                      placeholder={"Select Shift"}
                      form={form}
                      type="select"
                      selectOption={SHIFT_SCHEDULE_OPTIONS}
                      label={"Shift"}
                      required={true}
                    />

                    <FormInputField
                      name="salary_type"
                      style="bg-cblack3"
                      placeholder={"Salary Type"}
                      form={form}
                      type="select"
                      selectOption={SALARY_TYPE_OPTIONS}
                      label={"Salary Type"}
                      required={true}
                    />

                    <FormInputField
                      name="salary"
                      placeholder={"Salary"}
                      form={form}
                      type="text"
                      style="bg-cblack3"
                      label={"Salary Amount"}
                      required={true}
                    />
                    <FormInputField
                      name="bank_account_number"
                      style="bg-cblack3"
                      placeholder={"Account Number"}
                      form={form}
                      type="text"
                      label={"Bank AC/Account Number"}
                      required={true}
                    />
                    <FormInputField
                      name="bank_name"
                      placeholder={"Bank Name"}
                      form={form}
                      type="text"
                      style="bg-cblack3"
                      label={"Bank Name"}
                      required={true}
                    />
                    <FormInputField
                      name="bank_branch_name"
                      placeholder={"Branch Name"}
                      form={form}
                      type="text"
                      style="bg-cblack3"
                      label={"Bank Branch Name"}
                      required={true}
                    />

                    <FormInputField
                      name="currency"
                      style="bg-cblack3"
                      placeholder={"Currency"}
                      form={form}
                      type="select"
                      selectOption={CURRENCY_OPTIONS}
                      label={"Currency Type"}
                      required={true}
                    />

                    <FormInputField
                      name="passcode"
                      placeholder={"4-6 digit pin"}
                      form={form}
                      type="password"
                      style="bg-cblack3"
                      label={"Pin"}
                      required={true}
                    />
                  </div>
                  <div className="mt-4 flex gap-5 w-full">
                    <Link
                      className="w-full"
                      to={"/backoffice/staff-management/staff/all"}
                    >
                      <Button className="w-full bg-cred text-white">
                        Cancel
                      </Button>
                    </Link>

                    <Button isLoading={loading} className="w-full bg-cyellow">
                      Confirm
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          </CardContent>
        </Card>

        <SelectShiftDialog
          shifts={shifts}
          isOpen={openShiftDialog}
          selectedShift={selectedShift}
          setSelectedShift={setSelectedShift}
          onClose={offshiftdialog}
          setShiftValue={setShiftValue}
        />
      </div>
    </Layout2>
  );
}

export default AddNewStaff;
